import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import Footer from './Footer';
import Pagination from './Pagination';
import Header from './Header';
import NativeAdSection from './NativeAdSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Video Card Component
const VideoCard = ({ video, onVideoClick }) => {
  return (
    <Card 
      className="video-card border-orange-500/10 cursor-pointer group overflow-hidden"
      onClick={() => onVideoClick(video.id)}
      data-testid={`video-card-${video.id}`}
    >
      <div className="relative aspect-video">
        <img
          src={video.thumbnail_url || `https://picsum.photos/400/225?random=${video.id}`}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded text-xs text-white backdrop-blur-sm">
          {video.duration ? `${Math.floor(video.duration / 60)}:${(video.duration % 60).toString().padStart(2, '0')}` : '10:30'}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
          <div className="play-button-overlay w-16 h-16 rounded-full flex items-center justify-center animate-bounce">
            <div className="w-0 h-0 border-l-[20px] border-l-white border-y-[12px] border-y-transparent ml-1" />
          </div>
        </div>
        <div className="absolute top-2 left-2">
          <Badge className="bg-orange-600/90 text-white text-xs">⭐ Featured</Badge>
        </div>
      </div>
      <CardContent className="p-4">
        <h3 className="text-white font-semibold mb-2 line-clamp-2 group-hover:text-orange-400 transition-colors">
          {video.title}
        </h3>
        <div className="flex items-center justify-between text-sm text-gray-400">
          <span>{video.view_count.toLocaleString()} views</span>
          <div className="flex items-center space-x-1">
            <span className="text-orange-400">⭐</span>
            <span>{video.rating}</span>
          </div>
        </div>
        {video.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {video.tags.slice(0, 3).map((tag, index) => (
              <Badge key={index} variant="secondary" className="text-xs bg-orange-600/20 text-orange-300 border-orange-500/30">
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const FeaturedPage = () => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const videosPerPage = 20;

  useEffect(() => {
    fetchFeaturedVideos();
  }, [currentPage]);

  const fetchFeaturedVideos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/videos`, {
        params: {
          featured: true,
          page: currentPage,
          limit: videosPerPage
        }
      });

      setVideos(response.data.items || response.data);
      setTotalPages(response.data.total_pages || Math.ceil((response.data.items || response.data).length / videosPerPage));
    } catch (error) {
      console.error('Failed to fetch featured videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoClick = (videoId) => {
    navigate(`/video/${videoId}`);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen" data-testid="featured-page">
      <Header showNavigation={true} />

      <main className="container mx-auto px-6 py-8">
        {/* Page Title */}
        <div className="mb-8 text-center pt-8">
          <h1 className="text-4xl font-bold title-primary mb-4 flex items-center justify-center">
            <span className="mr-3 text-5xl">🎬</span>
            Featured Videos
          </h1>
          <p className="text-gray-400 text-lg">
            Discover our hand-picked premium content featuring the best videos on Megavideosee
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-white text-xl animate-pulse">Loading featured videos...</div>
          </div>
        ) : (
          <>
            {videos.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-slideInUp">
                  {videos.map((video) => (
                    <VideoCard 
                      key={video.id} 
                      video={video} 
                      onVideoClick={handleVideoClick} 
                    />
                  ))}
                </div>
                
                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </>
            ) : (
              <div className="text-center py-20 animate-fadeIn">
                <div className="text-6xl mb-4">⭐</div>
                <div className="text-xl text-gray-400 mb-2">No featured videos available</div>
                <div className="text-gray-500">Featured videos will appear here once they're selected by our team</div>
              </div>
            )}
          </>
        )}
        
        {/* Native Ad Section */}
        <NativeAdSection />
      </main>

      <Footer />
    </div>
  );
};

export default FeaturedPage;